import React from 'react';
import { useSiteConfig } from '../hooks/useSiteConfig';
import { useServices } from '../hooks/useServices';
import Editable from '../components/Editable';
import './Pages.css';

const DEFAULT_SERVICES = [
  {
    title: 'Custom Woodwork',
    description: 'Handcrafted wooden items made with care, from functional pieces to decorative accents.',
    bullets: [
      'Planters, cutting boards, trellises, clocks, baskets, and more',
      'Options for natural finish or custom laser engraving',
      'Unique designs tailored to your style',
      'Durable, high-quality craftsmanship',
    ],
  },
  {
    title: 'Laser Engraving',
    description: 'Add a personal touch to wood items, gifts, and décor with precise laser engraving.',
    bullets: [
      'Custom text, logos, or artwork',
      'Works on cutting boards, signs, jewelry, and more',
      'Can be applied to pre-made or new pieces',
      'Detailed and long-lasting engraving',
    ],
  },
  {
    title: 'Handmade Greeting Cards',
    description: 'Unique, handcrafted cards perfect for any occasion.',
    bullets: [
      'Personalized messages and designs',
      'Variety of styles, from whimsical to elegant',
      'High-quality paper and materials',
      'Made-to-order for birthdays, holidays, and special moments',
    ],
  },
  {
    title: 'Jewelry & Small Wooden Gifts',
    description: 'Handmade items that combine wood, design, and personal flair.',
    bullets: [
      'Wooden jewelry, magnets, and keepsakes',
      'Customizable designs and engravings',
      'Perfect for gifts or personal use',
      'Carefully handcrafted for quality and uniqueness',
    ],
  },
  {
    title: 'Cold Process Soaps & Scented Wax Melts',
    description: 'Artisan soaps and wax melts that delight the senses and make thoughtful gifts.',
    bullets: [
      'Handcrafted with natural ingredients',
      'Unique scents and designs',
      'Ideal for personal use or gifting',
      'Made with care and attention to detail',
    ],
  },
  {
    title: 'Personalized Gifts & Home Décor',
    description: 'Custom, meaningful pieces that add charm and personality to any space.',
    bullets: [
      'Signs, décor items, and functional wood pieces',
      'Personalized options to fit your style',
      'Perfect for gifts, weddings, or special occasions',
      'Handmade with care and attention to detail',
    ],
  },
];

const DEFAULT_PROCESS = [
  { step: '01', title: 'Share', description: 'Tell us your ideas and vision.' },
  { step: '02', title: 'Design', description: 'We plan and confirm your custom piece.' },
  { step: '03', title: 'Craft', description: 'Handmade with care and quality materials.' },
  { step: '04', title: 'Enjoy', description: 'Receive and treasure your unique creation.' },
];

export default function Services() {
  const { items } = useServices();
  const { config } = useSiteConfig();

  const services = items && items.length > 0 ? items : DEFAULT_SERVICES;
  const process = Array.isArray(config.services_process) && config.services_process.length
    ? config.services_process
    : DEFAULT_PROCESS;

  return (
    <main className="page-content">
      <section className="page-hero">
        <div className="container">
          <h1>Our Services</h1>
        </div>
      </section>

      <Editable descriptor={{ kind: 'services', label: 'Services list' }}>
      <section className="container page-section">
        <div className="services-grid">
          {services.map((s, i) => (
            <div className="service-card" key={s.id || i}>
              <h3>{s.title}</h3>
              {s.description && <p>{s.description}</p>}
              {Array.isArray(s.bullets) && s.bullets.length > 0 && (
                <ul>
                  {s.bullets.map((b, j) => <li key={j}>{b}</li>)}
                </ul>
              )}
            </div>
          ))}
        </div>
      </section>
      </Editable>

      <Editable descriptor={{ kind: 'config', key: 'services_process', label: 'Our Process steps' }}>
      <section className="values-section">
        <div className="container">
          <h2>Our Process</h2>
          <div className="process-grid">
            {process.map((p, i) => (
              <div className="process-step" key={i}>
                <div className="step-number">{p.step}</div>
                <h3>{p.title}</h3>
                <p>{p.description}</p>
              </div>
            ))}
          </div>
        </div>
      </section>
      </Editable>
    </main>
  );
}
